import { HISTORY_ACTION_MODE, NodeApi, useEditorReturnType } from '@candulabs/craft';
import { nanoid } from 'nanoid';

import { Column, COLUMN_DEFAULT_STYLES, ColumnCustom } from './Column';
import { Direction } from './types';

const { ADD_TO_TIMELINE, MERGE } = HISTORY_ACTION_MODE;

const nodeWidth = (nodeApi: NodeApi): number =>
  parseInt(nodeApi?.node.data.custom?.custom?.width, 10);

export const createColumnOperations = (
  parentNodeId: string,
  { actions, query }: useEditorReturnType,
) => {
  const addColumn = (
    width: number,
    offset?: number,
    historyMode: HISTORY_ACTION_MODE = ADD_TO_TIMELINE,
  ) => {
    return actions.addNodes(
      [
        query
          .parseFreshNode({
            data: {
              type: Column,
              props: { id: nanoid(6) },
              custom: {
                custom: { width: `${width}` },
                css: COLUMN_DEFAULT_STYLES,
              },
            },
          })
          .toNode(),
      ],
      parentNodeId,
      offset,
      historyMode,
    );
  };

  const resizeColumn = (
    nodeApi: NodeApi,
    width: number,
    historyMode: HISTORY_ACTION_MODE = ADD_TO_TIMELINE,
  ) => {
    actions.setCustom(
      nodeApi.node.id,
      (custom: ColumnCustom) => {
        // eslint-disable-next-line no-param-reassign
        custom.custom.width = `${width}`;
      },
      historyMode,
    );

    return true;
  };

  return {
    getColumnWidths: () => {
      const parentData = query.node(parentNodeId).get().data;
      return parentData.nodes.map((node: string) => nodeWidth(query.node(node)));
    },

    setColumnSize: (offset: number, width: number, historyMode?: HISTORY_ACTION_MODE) => {
      const parentData = query.node(parentNodeId).get().data;
      return resizeColumn(query.node(parentData.nodes[offset]), width, historyMode);
    },

    newColumn: (addAtOffset?: number) => {
      const parentData = query.node(parentNodeId).get().data;
      const columnIds = parentData.nodes;
      const commonWidth = columnIds.reduce(
        (acc: number, node: string) => (nodeWidth(query.node(node)) === acc ? acc : false),
        nodeWidth(query.node(columnIds[0])),
      );

      const newLength = columnIds.length + 1;
      if (commonWidth && 12 % newLength === 0) {
        // if we're in the golden zone of evenly spaced columns, we can add a proportional one
        const newWidth = 12 / newLength;

        // add our column to the end or to the specified offset
        addColumn(newWidth, addAtOffset, ADD_TO_TIMELINE);

        columnIds.forEach((id) => resizeColumn(query.node(id), newWidth, MERGE));
      } else {
        // otherwise we're adding a width '1' column, subtract this from the most convenient neighbour
        const reversedOffset = columnIds
          .slice()
          .reverse()
          .findIndex((id: string) => nodeWidth(query.node(id)) > 1);

        if (reversedOffset === -1) {
          return;
        }

        const toShrink = query.node(columnIds[columnIds.length - reversedOffset - 1]);
        resizeColumn(toShrink, nodeWidth(toShrink) - 1, ADD_TO_TIMELINE);

        // add our column to the end
        addColumn(1, addAtOffset, MERGE);
      }
    },

    growColumn: (id: string, direction: Direction, size = 1) => {
      const parentData = query.node(parentNodeId).get().data;
      const offset = parentData.nodes.findIndex((childNodeId) => childNodeId === id);

      // shrink sibling at offset + direction
      const sibling = query.node(parentData.nodes[offset + direction]);
      if (!sibling) {
        return false;
      }

      const siblingWidth = nodeWidth(sibling);
      // we can only grow if we're next to a sibling with space
      if (siblingWidth <= size) {
        return false;
      }

      // shrink sibling
      resizeColumn(sibling, siblingWidth - size);

      // grow node
      const currentNode = query.node(id);
      const newWidth = nodeWidth(currentNode) + size;
      resizeColumn(currentNode, newWidth, MERGE);

      return true;
    },

    shrinkColumn: (id: string, direction: Direction, size = 1) => {
      const parentData = query.node(parentNodeId).get().data;
      const offset = parentData.nodes.findIndex((childNodeId) => childNodeId === id);

      // we can only shrink if we're large enough
      const currentNode = query.node(id);
      const currentWidth = nodeWidth(currentNode);
      if (currentWidth <= size) {
        return false;
      }

      // grow sibling at offset + direction
      const sibling = query.node(parentData.nodes[offset + direction]);
      if (!sibling) {
        return false;
      }

      // grow sibling
      const siblingWidth = nodeWidth(sibling);
      resizeColumn(sibling, siblingWidth + size);

      // shrink self
      resizeColumn(currentNode, currentWidth - size, MERGE);

      return true;
    },

    increaseColumn: (nodeId: string) => {
      const parentData = query.node(parentNodeId).get().data;
      const columnIds = parentData.nodes;
      const offset = columnIds.findIndex((childNodeId) => childNodeId === nodeId);
      const node = query.node(columnIds[offset]);

      const findSiblingRight = () => {
        let counter = offset + 1;
        while (counter < columnIds.length) {
          const match = query.node(columnIds[counter]);
          if (nodeWidth(match) > 1) {
            return match;
          }
          counter += 1;
        }
        return null;
      };
      const findSiblingLeft = () => {
        let counter = offset - 1;
        while (counter > -1) {
          const match = query.node(columnIds[counter]);
          if (nodeWidth(match) > 1) {
            return match;
          }
          counter -= 1;
        }
        return null;
      };
      // column to shrink
      const nextAvailableSibling = findSiblingRight() || findSiblingLeft();
      if (nextAvailableSibling === null) {
        return false;
      }

      resizeColumn(node, nodeWidth(node) + 1);
      resizeColumn(nextAvailableSibling, nodeWidth(nextAvailableSibling) - 1, MERGE);

      return true;
    },

    decreaseColumn: (nodeId: string) => {
      const parentData = query.node(parentNodeId).get().data;
      const columnIds = parentData.nodes;
      const offset = columnIds.findIndex((childNodeId) => childNodeId === nodeId);
      const node = query.node(columnIds[offset]);

      const offsetNodeWidth = nodeWidth(node);
      if (offsetNodeWidth < 2) {
        return false;
      }

      const findSiblingRight = () => {
        if (offset + 1 >= columnIds.length) {
          return null;
        }
        return query.node(columnIds[offset + 1]);
      };
      const findSiblingLeft = () => {
        if (offset - 1 < 0) {
          return null;
        }
        return query.node(columnIds[offset - 1]);
      };
      // first, we look right then left
      const nextAvailableSibling = findSiblingRight() || findSiblingLeft();
      if (nextAvailableSibling === null) {
        return false;
      }

      resizeColumn(node, offsetNodeWidth - 1);
      resizeColumn(nextAvailableSibling, nodeWidth(nextAvailableSibling) + 1, MERGE);

      return true;
    },

    spaceEvenly: (historyActionMode: HISTORY_ACTION_MODE = ADD_TO_TIMELINE) => {
      const parentData = query.node(parentNodeId).get().data;
      const columnNodeIds = parentData.nodes;
      const numberOfChildren = columnNodeIds.length;
      if (numberOfChildren < 1) {
        // sanity check
        return;
      }
      const isEvenlySpaced =
        [1, 2, 3, 4, 6, 12].indexOf(columnNodeIds.length) > -1 || numberOfChildren > 12;
      const safeNumberOfChildren = Math.min(12, numberOfChildren);
      actions.produceState((state, noHistoryActions) => {
        columnNodeIds.forEach((columnNodeId, index) => {
          // just in case we end up with more than 12 columns, we remove the last columns.
          // this should never happen in prod, but it useful in testing and just some defensive programming
          if (index > 11) {
            noHistoryActions.deleteNodes([columnNodeId]);
          }
          noHistoryActions.setCustom(columnNodeId, (custom: ColumnCustom) => {
            // this width logic is a little funny. All the non isEvenlySpaced
            // handle whenever we have 5,7,8,9,10 or 11 columns.
            // 5 is a bit of a wierd case, as we allocate 3, 3, 2, 2, 2 columns.
            // owherwise, we allocate 2 and then 1 for each column
            let width = 1;
            if (isEvenlySpaced) {
              width = 12 / safeNumberOfChildren;
            } else if (numberOfChildren === 5 && index < 2) {
              width = 3; // 5 is a bit of a special case....
            } else if (index < 12 - safeNumberOfChildren) {
              width = 2;
            }
            // eslint-disable-next-line no-param-reassign
            custom.custom.width = `${width}`;
          });
        });
      }, historyActionMode);
    },
  };
};
